/**
 * V2 cart drawer behaviour (AB#527).
 *
 * Opens on the header cart button, on the tab bar's cart tab, and automatically after a
 * successful add from quick add. Horizon's own drawer is intercepted in the CAPTURE phase and
 * must never open: it is still in the DOM, and two drawers racing to show the same cart is worse
 * than either one.
 *
 * Quantity changes go through /cart/change.js. The response is the SOURCE OF TRUTH: if Shopify
 * clamps a quantity to available stock, the input is set to what Shopify returned and the reason
 * is shown inline. The stepper never silently reverts.
 */

const SHEET_ID = 'mlr-cart-drawer';

const sheet = () => document.getElementById(SHEET_ID);

/* ------------------------------------------------------------------ open/close */

/**
 * ONE OWNER: Horizon's <theme-drawer>. It opens and it closes, and nothing else touches the
 * dialog (AB#549).
 *
 * THE LIVE BUG THIS FIXES. The cart dialog would not close. Its X was the hit target, the close
 * was routed correctly, and `theme-drawer.close()` really was called -- measured, one call --
 * and `dialog.open` stayed true. `close()` begins `if (!this.isOpen) return;` and `isOpen` reads
 * the element's `open` ATTRIBUTE, which only `theme-drawer.open()` sets.
 *
 * This function used to reach for a declarative opener first, with a comment saying that doing
 * so "keeps focus handling in one place rather than giving the dialog two owners". The intent
 * was right. The element it looked for, `[data-mlr-sheet-open="mlr-cart-drawer"]`, DOES NOT
 * EXIST: the cart is opened by intercepting Horizon's header button, not by a declarative
 * opener. So the lookup always failed and the silent fallback ran instead --
 * `dialog.showModal()` -- which opens the dialog behind the component's back. The drawer then
 * had no idea it was open and politely declined to close it.
 *
 * So the fallback is gone rather than fixed. A fallback that opens a dialog by a route its owner
 * cannot see is not a safety net, it is the failure. If `theme-drawer` has not upgraded yet this
 * returns false and the click is a no-op, which is recoverable; a dialog that cannot be closed
 * is not.
 *
 * Calling `open()` also restores what the bypass quietly dropped: the focus trap, the scroll
 * lock and focus returning to whatever opened it.
 */
function openDrawer() {
  const el = sheet();
  if (!el || typeof (/** @type {any} */ (el).open) !== 'function') return false;
  /** @type {any} */ (el).open();
  return true;
}

function closeDrawer() {
  const el = sheet();
  if (!el || typeof (/** @type {any} */ (el).close) !== 'function') return;
  /** @type {any} */ (el).close();
}

/**
 * Intercept Horizon's cart trigger.
 *
 * The header button carries `on:click="#cart-drawer/toggle"`, which Horizon binds on the
 * document. Capture phase, and `stopPropagation`, so ours opens and Horizon's never sees the
 * click. Same pattern the menu sheet uses for the menu drawer.
 */
document.addEventListener(
  'click',
  (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const trigger = target.closest(
      '[data-testid="cart-drawer-trigger"], [aria-controls="cart-drawer"], [data-mlr-cart-open]'
    );
    if (!trigger) return;
    if (!sheet()) return; // no V2 drawer on this page: let Horizon have it

    event.preventDefault();
    event.stopPropagation();
    openDrawer();
  },
  true
);

/* ------------------------------------------------------------------ refresh */

/**
 * Re-render the drawer from the server.
 *
 * The whole section is fetched rather than patched in place, so the subtotal, the discount list
 * and Shopify's tax line all come back consistent with each other. Patching the DOM piecemeal is
 * how a drawer ends up showing a stale total next to a fresh line.
 */
async function refresh() {
  try {
    // `/cart?view=drawer`, NOT `?section_id=`. The drawer is a section inside the footer section
    // group, and a grouped section cannot be re-rendered that way: the request 404s. The
    // alternate cart template returns the same markup from the same snippet.
    const res = await fetch('/cart?view=drawer', { headers: { Accept: 'text/html' } });
    if (!res.ok) return;
    const html = await res.text();
    const parsed = new DOMParser().parseFromString(html, 'text/html');

    const freshBody = parsed.querySelector('[data-mlr-cart]');
    const liveBody = document.querySelector('[data-mlr-cart]');
    if (freshBody && liveBody) liveBody.replaceWith(freshBody);

    const freshFoot = parsed.querySelector('.mlr-cart__foot');
    const liveFoot = document.querySelector('.mlr-cart__foot');
    if (freshFoot && liveFoot) liveFoot.replaceWith(freshFoot);
    else if (freshFoot && !liveFoot) {
      document.querySelector(`#${SHEET_ID} .mlr-sheet__footer`)?.append(freshFoot);
    } else if (!freshFoot && liveFoot) {
      liveFoot.remove();
    }

    updateBubble(parsed);
  } catch {
    /* a failed refresh leaves the drawer as it was, which is better than emptying it */
  }
}

function updateBubble(parsed) {
  const count = Number(parsed?.querySelector('[data-mlr-cart]')?.getAttribute('data-item-count') ?? NaN);
  if (Number.isNaN(count)) return;
  for (const el of document.querySelectorAll('[data-count], .cart-count')) {
    el.textContent = String(count);
    el.classList.toggle('on', count > 0);
  }
}

/* ------------------------------------------------------------------ quantity */

function lineError(lineEl, message) {
  const slot = lineEl?.querySelector('[data-mlr-line-error]');
  if (slot) slot.textContent = message ?? '';
}

async function changeLine(line, quantity, lineEl) {
  lineError(lineEl, '');
  try {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line: Number(line), quantity: Number(quantity) }),
    });

    const cart = await res.json().catch(() => null);

    if (!res.ok) {
      lineError(lineEl, cart?.description ?? cart?.message ?? 'That quantity is not available.');
      await refresh();
      return;
    }

    // Shopify is the source of truth. If it clamped the quantity, say so rather than letting the
    // stepper spring back with no explanation.
    const item = cart?.items?.[Number(line) - 1];
    const input = lineEl?.querySelector('[data-mlr-qty-input]');
    if (item && input && Number(input.value) !== item.quantity) {
      input.value = String(item.quantity);
      lineError(lineEl, `Only ${item.quantity} available.`);
    }

    await refresh();
  } catch {
    lineError(lineEl, 'Could not update the cart. Try again.');
  }
}

document.addEventListener('click', async (event) => {
  const target = event.target instanceof Element ? event.target : null;
  if (!target) return;

  const up = target.closest('[data-mlr-qty-up]');
  const down = target.closest('[data-mlr-qty-down]');
  const remove = target.closest('[data-mlr-remove]');
  if (!up && !down && !remove) return;

  const lineEl = target.closest('[data-mlr-cart-line]');
  const input = lineEl?.querySelector('[data-mlr-qty-input]');
  const line = (up ?? down ?? remove).getAttribute('data-line');
  const current = Number(input?.value ?? 0);

  if (remove) {
    await changeLine(line, 0, lineEl);
    return;
  }
  const next = up ? current + 1 : Math.max(0, current - 1);
  if (input) input.value = String(next);
  await changeLine(line, next, lineEl);
});

document.addEventListener('change', async (event) => {
  const input = event.target instanceof Element ? event.target.closest('[data-mlr-qty-input]') : null;
  if (!input) return;
  const lineEl = input.closest('[data-mlr-cart-line]');
  await changeLine(input.getAttribute('data-line'), Math.max(0, Number(input.value) || 0), lineEl);
});

/* ------------------------------------------------------------------ discount */

document.addEventListener('click', async (event) => {
  const button = event.target instanceof Element ? event.target.closest('[data-mlr-discount-apply]') : null;
  if (!button) return;

  const form = button.closest('form');
  const input = form?.querySelector('[data-mlr-discount-input]');
  const error = form?.querySelector('[data-mlr-discount-error]');
  const code = (input?.value ?? '').trim();
  if (error) error.textContent = '';
  if (!code) return;

  try {
    // Shopify validates the code; nothing about it is decided here.
    const res = await fetch(`/discount/${encodeURIComponent(code)}`, { method: 'GET', redirect: 'follow' });
    await refresh();

    // RE-QUERY after the refresh. `refresh()` replaces `.mlr-cart__foot`, which is where the
    // error element lives, so a message written before it was silently thrown away -- the code
    // failed, the drawer said nothing, and the test that caught it was right to.
    const freshError = document.querySelector('[data-mlr-discount-error]');
    const freshInput = document.querySelector('[data-mlr-discount-input]');
    const applied = document.querySelector('.mlr-cart__discounts');

    if (!res.ok || !applied) {
      if (freshError) freshError.textContent = 'That code is not valid for this cart.';
      if (freshInput) freshInput.value = code;
    } else if (freshInput) {
      freshInput.value = '';
    }
  } catch {
    const freshError = document.querySelector('[data-mlr-discount-error]');
    if (freshError) freshError.textContent = 'Could not apply that code. Try again.';
  }
});

/* ------------------------------------------------------------------ auto-open */

/**
 * Open after a successful add. Listens to the same events Horizon's drawer consumed, plus the
 * one quick add dispatches, so the PDP's add button works here with no further wiring.
 */
for (const name of ['mlr:cart-added', 'cart:refresh', 'cart:update', 'variant:add']) {
  document.addEventListener(name, async () => {
    await refresh();
    openDrawer();
  });
}

/** Horizon's drawer must never open alongside ours. */
const horizonDrawer = document.getElementById('cart-drawer');
if (horizonDrawer) {
  new MutationObserver(() => {
    const open = horizonDrawer.hasAttribute('open') || horizonDrawer.querySelector('dialog[open]');
    if (open && sheet()) {
      horizonDrawer.removeAttribute('open');
      horizonDrawer.querySelector('dialog[open]')?.close?.();
    }
  }).observe(horizonDrawer, { attributes: true, subtree: true, attributeFilter: ['open'] });
}

export { openDrawer, closeDrawer, refresh };
